import React, { useRef } from 'react';
import { Form, Input, Switch, Button, Card, Space, message, Upload } from 'antd';
import { useTranslation } from 'react-i18next';
import { CertificateSettings } from '../../../../types/instanceTypes';
import SignaturePad from '../../../../components/Common/ui/SignaturePad';
import { Link } from 'react-router-dom';
import { useCertificateSettings } from '../hooks/useCertificateSettings';
import { useDispatch } from 'react-redux';
import { updateWorkspaceLogo } from '../../../../redux/slicers/authSlice';
import ImageWithoutBasePath from 'components/Common/imageWithoutBasePath';

interface Props {
  initialData?: CertificateSettings;
  onSubmit: (data: CertificateSettings) => Promise<void>;
  isSubmitting: boolean;
  isUploadingSignature: boolean;
  handleSignatureUpload: (file: File) => Promise<string>;
  isUploadingLogo?: boolean;
}

export const CertificateSettingsForm: React.FC<Props> = ({
  initialData,
  onSubmit,
  isSubmitting,
  handleSignatureUpload,
  isUploadingLogo = false,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const maxSizeMB = process.env.REACT_APP_MAX_FILE_SIZE_MB;


  const signaturePadRef = useRef<any>(null);
  const [logoPreview, setLogoPreview] = React.useState<string | null>(null);
  const [loadingLogo, setLoadingLogo] = React.useState(false);
  const dispatch = useDispatch();

  const {
    handleLogoUpload
  } = useCertificateSettings();

  React.useEffect(() => {
    if (initialData) {
      // Only set directorSignatureUrl if it exists
      const fields: any = { ...initialData };
      if (!initialData.directorSignatureUrl) {
        delete fields.directorSignatureUrl;
      }
      if (initialData.logo) {
        setLogoPreview(initialData.logo);
      }
      form.setFieldsValue(fields);
    }
    // Only depend on initialData to avoid unnecessary resets
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  const handleLogoUpdate = async (file: File) => {
    setLoadingLogo(true);
    if (!handleLogoUpload) return;

    try {
      const newLogo = await handleLogoUpload(file);
      if (typeof newLogo === 'string') {
        setLogoPreview(newLogo);
        dispatch(updateWorkspaceLogo({ newLogo }));
      }
      setLoadingLogo(false);
    } catch (error) {
      setLoadingLogo(false);
      return;
    }
  };

  const handleLogoRemoval = async () => {
    if (!handleLogoUpload) return;

    try {
      await handleLogoUpload(null);
      setLogoPreview(null);
      dispatch(updateWorkspaceLogo({ newLogo: null }));
    } catch (error) {
      return;
    }
  };

  const handleFinish = async (values: CertificateSettings) => {
    let signatureUrl = initialData?.directorSignatureUrl || "";
    let shouldUpdateSignature = false;

    if (signaturePadRef.current) {
      if (!signaturePadRef.current.isEmpty()) {
        const file = await signaturePadRef.current.getSignatureFile();
        if (file) {
          try {
            signatureUrl = await handleSignatureUpload(file);
            shouldUpdateSignature = true;
          } catch (error) {
            message.error(t('certificate.messages.signatureUploadError'));
            return;
          }
        }
      }
    }

    if (shouldUpdateSignature || initialData?.directorSignatureUrl) {
      values.directorSignatureUrl = signatureUrl;
    } else {
      delete values.directorSignatureUrl;
    }

    // Ensure we never send an empty string
    if (!values.directorSignatureUrl) {
      delete values.directorSignatureUrl;
    }

    try {
      await onSubmit(values);
      message.success(t('certificate.messages.updateSuccess'));
    } catch (error) {
      message.error(t('certificate.messages.updateError'));
    }
  };

  return (
    <Card>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          certificateEnabled: false,
          ...initialData
        }}
      >
        {/* Logo Upload Section */}
        <div className="card-body">
          <div className="settings-profile-upload">
            <span style={{
              width: 120,
              height: 120,
              borderRadius: "8px",
              overflow: "hidden",
              position: "relative",
              backgroundColor: "#f5f5f5",
              border: "2px solid #eee",
              display: "inline-block",
              margin: "5px"
            }}>

              <ImageWithoutBasePath
                loading={loadingLogo}
                src={
                  logoPreview ||
                  "/assets/img/profiles/fake-image-instance.jpg"
                }
                alt="Profile"
                width={150}
                height={150}
                style={{
                  objectFit: "cover",
                  width: "100%",
                  height: "100%",
                  display: "block",
                }}
              />


            </span>
            <div className="title-upload">
              {logoPreview && (
                <Link to="#" className="me-2 text-danger" onClick={handleLogoRemoval}>
                  {t("remove")}
                </Link>
              )}
              <Upload
                disabled={loadingLogo}
                customRequest={({ file, onSuccess }) => {
                  handleLogoUpdate(file as File);
                  if (onSuccess) setTimeout(() => onSuccess("ok"), 0);
                }}
                showUploadList={false}
                accept=".jpg,.jpeg,.png,.webp"
              >
                <Link to="#" className="text-primary">{t("update")}</Link>
              </Upload>
            </div>
          </div>

          <div className="profile-uploader profile-uploader-two mb-3">
            <Upload.Dragger
              disabled={isUploadingLogo}
              customRequest={({ file, onSuccess }) => {
                handleLogoUpdate(file as File);
                if (onSuccess) setTimeout(() => onSuccess("ok"), 0);
              }}
              showUploadList={false}
              accept=".jpg,.jpeg,.png,.svg"
            >
              <span className="upload-icon">
                <i className="ti ti-upload" />
              </span>
              <div className="drag-upload-btn bg-transparent me-0 border-0">
                <h6 className="upload-btn">
                  -{t("certificate.form.logo.maxFileSize", { maxSize: maxSizeMB })}
                  <br />
                  -{t("certificate.form.logo.clickToUpload", { format: "jpg,jpeg,png,webp" })}
                  <br />
                  -{t("certificate.form.logo.recommendation", { width: 300, height: 300 })}
                </h6>
              </div>
            </Upload.Dragger>
          </div>
        </div>

        {/* Form Fields */}
        <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(2, 1fr)' }}>
          <Form.Item
            name="establishmentName"
            label={t('certificate.form.establishmentName.label')}
            rules={[{ required: true, message: t('certificate.form.establishmentName.required') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="phoneNumber"
            label={t('certificate.form.phoneNumber.label')}
            rules={[{ required: true, message: t('certificate.form.phoneNumber.required') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="address"
            label={t('certificate.form.address.label')}
            rules={[{ required: true, message: t('certificate.form.address.required') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="zipCode"
            label={t('certificate.form.zipCode.label')}
            rules={[{ required: true, message: t('certificate.form.zipCode.required') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="academy"
            label={t('certificate.form.academy.label')}
            rules={[{ required: true, message: t('certificate.form.academy.required') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="prefecture"
            label={t('certificate.form.prefecture.label')}
            rules={[{ required: true, message: t('certificate.form.prefecture.required') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="commune"
            label={t('certificate.form.commune.label')}
            rules={[{ required: true, message: t('certificate.form.commune.required') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="directorName"
            label={t('certificate.form.directorName.label')}
            rules={[{ required: true, message: t('certificate.form.directorName.required') }]}
          >
            <Input />
          </Form.Item>

          {/* <Form.Item
            name="certificateEnabled"
            label={t('certificate.form.enableGeneration.label')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label={t('certificate.form.directorSignature.label')}
            name="directorSignatureUrl"
          >
            {initialData?.directorSignatureUrl && (
              <div style={{ marginBottom: 8 }}>
                <img
                  src={`${initialData.directorSignatureUrl}?v=${Date.now()}`}
                  alt={t('certificate.form.directorSignature.label')}
                  style={{ maxWidth: 300, maxHeight: 100, display: 'block', border: '1px solid #eee', background: '#fafafa' }}
                />
                <div style={{ fontSize: 12, color: '#888' }}>{t('certificate.form.directorSignature.current')}</div>
              </div>
            )}
            <SignaturePad
              ref={signaturePadRef}
              loading={isUploadingSignature}
            />
          </Form.Item> */}
        </div>

        <Space style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Button type="primary" className="btn-primary" htmlType="submit" loading={isSubmitting}>
            {isSubmitting ? t('common.saving') : t('certificate.form.submit')}
          </Button>
        </Space>
      </Form>
    </Card>
  );
};